import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Catches page-chunk load failures. These happen after a new release: the open
 * tab still points at the previous build's files, which no longer exist, and the
 * browser caches the failed import — so a top-bar click "does nothing" until a
 * refresh. Recovery: reload once (guarded by a short window so it can never
 * loop), and always reset when the user navigates, so one failure can never
 * freeze the app for the rest of the session.
 */
const RELOAD_KEY = 'lazy_reload_at';
const RELOAD_WINDOW_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  const msg = String((error as Error)?.message ?? error ?? '');
  return /dynamically imported module|Failed to fetch|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(msg);
}

/** Reload to pick up the current release, at most once per window (no loops). */
export function reloadForFreshBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch { /* storage blocked: still reload once */ }
  window.location.reload();
  return true;
}

if (typeof window !== 'undefined') {
  // Vite reports failed preloads here (including hover prefetches) before React sees them.
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault();
    reloadForFreshBuild();
  });
}

interface Props { children: ReactNode; resetKey: string }
interface State { hasError: boolean }

class Boundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State | null {
    return isChunkLoadError(error) ? { hasError: true } : null;
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) reloadForFreshBuild();
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) this.setState({ hasError: false });
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export default function LazyLoadErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
}
